import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import DataService from '../../services/data.service';
import { toast } from 'react-toastify';
import LoaderOverlay from '../common/loader.component';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import AnimateOnScroll from '../common/animate';
import './review.css';

const Review = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    rating: 0,
    review: '',
    customerName: '',
    reviewTitle: ''
  });
  const [errors, setErrors] = useState({});
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    if (token) {
      fetchBookingDetails();
    } else {
      toast.error('Invalid review link');
      setTimeout(() => navigate('/'), 3000);
    }
  }, [token]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await DataService.getBookingByToken({ token });
      if (response.data && response.data.booking) {
        setBookingData(response.data.booking);
        setFormData(prev => ({
          ...prev,
          customerName: response.data.booking.name || ''
        }));
      } else {
        toast.error('Booking not found or invalid link');
        setTimeout(() => navigate('/'), 3000);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Unable to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
    if (errors.rating) {
      setErrors({ ...errors, rating: '' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.rating) newErrors.rating = 'Please select a rating';
    if (!formData.review.trim()) newErrors.review = 'Please write a review';
    if (formData.review.trim().length < 10) newErrors.review = 'Review must be at least 10 characters';
    if (!formData.customerName.trim()) newErrors.customerName = 'Name is required';
    if (!formData.reviewTitle.trim()) newErrors.reviewTitle = 'Review title is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);
      await DataService.submitReview({
        token,
        ...formData,
        bookingId: bookingData?.booking_id
      });

      toast.success('Thank you for your review!');
      setSubmitted(true);

      // Redirect to home after 3 seconds
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5].map((star) => (
      <FontAwesomeIcon
        key={star}
        icon={faStar}
        className={`star ${star <= (hoverRating || formData.rating) ? 'active' : ''}`}
        onClick={() => handleRatingClick(star)}
        onMouseEnter={() => setHoverRating(star)}
        onMouseLeave={() => setHoverRating(0)}
      />
    ));
  };

  if (submitted) {
    return (
      <>
        <LoaderOverlay loading={loading} />
        <div>
          <Helmet>
            <title>Thank You | Divine Blush Beauty</title>
          </Helmet>

          <section
            className="hero-wrap hero-wrap-2"
            style={{ backgroundImage: "url('images/bb.jpg')" }}
            data-stellar-background-ratio="0.5"
          >
            <div className="overlay" />
            <div className="container">
              <div className="row no-gutters slider-text js-fullheight align-items-center justify-content-center">
                <AnimateOnScroll className="col-md-9 text-center">
                  <h1 className="mb-3 bread">Thank You</h1>
                  <p className="breadcrumbs">
                    <span className="mr-2"><Link to="/">Home</Link></span> Review
                  </p>
                </AnimateOnScroll>
              </div>
            </div>
          </section>

          <section className="ftco-section contact-section">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-md-8">
                  <div className="success-message text-center bg-light p-5 rounded">
                    <div className="success-icon mx-auto mb-4">✓</div>
                    <h2 className="heading mb-3">Thank You for Your Review!</h2>
                    <p className="mb-2">We appreciate your feedback and look forward to serving you again.</p>
                    <p className="redirect-text text-muted">Redirecting to homepage...</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </>
    );
  }

  return (
    <>
      <LoaderOverlay loading={loading} />
      <div>
        <Helmet>
          <title>Leave a Review | Divine Blush Beauty</title>
          <meta name="description" content="Share your experience with Divine Blush Beauty" />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <section
          className="hero-wrap hero-wrap-2"
          style={{ backgroundImage: "url('images/bb.jpg')" }}
          data-stellar-background-ratio="0.5"
        >
          <div className="overlay" />
          <div className="container">
            <div className="row no-gutters slider-text js-fullheight align-items-center justify-content-center">
              <AnimateOnScroll className="col-md-9 text-center">
                <h1 className="mb-3 bread">Leave a Review</h1>
                <p className="breadcrumbs">
                  <span className="mr-2"><Link to="/">Home</Link></span> Review
                </p>
              </AnimateOnScroll>
            </div>
          </div>
        </section>

        <section className="ftco-section contact-section">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="review-card bg-light p-4">
                  {/* Header */}
                  <div className="review-header text-center mb-4">
                    <h2 className="heading">How was your experience?</h2>
                    <p className="subtitle">We'd love to hear about your visit!</p>
                  </div>

                  {/* Booking Info */}
                  {bookingData && (
                    <div className="booking-info bg-white p-3 rounded mb-4">
                      <p className="mb-2"><strong>Date:</strong> {bookingData.date}</p>
                      <p className="mb-0"><strong>Time:</strong> {bookingData.time}</p>
                    </div>
                  )}

                  {/* Review Form */}
                  <form onSubmit={handleSubmit} className="review-form">
            {/* Rating */}
            <div className="form-group">
              <label>Your Rating *</label>
              <div className="star-rating">
                {renderStars()}
              </div>
              {formData.rating > 0 && (
                <p className="rating-text">
                  {formData.rating === 1 && 'Poor'}
                  {formData.rating === 2 && 'Fair'}
                  {formData.rating === 3 && 'Good'}
                  {formData.rating === 4 && 'Very Good'}
                  {formData.rating === 5 && 'Excellent'}
                </p>
              )}
              {errors.rating && <p className="error-text">{errors.rating}</p>}
            </div>

            {/* Customer Name */}
            <div className="form-group">
              <label>Your Name *</label>
              <input
                type="text"
                name="customerName"
                className="form-control"
                value={formData.customerName}
                onChange={handleChange}
                placeholder="Enter your name"
              />
              {errors.customerName && <p className="error-text">{errors.customerName}</p>}
            </div>

            {/* Review Title */}
            <div className="form-group">
              <label>Review Title *</label>
              <input
                type="text"
                name="reviewTitle"
                className="form-control"
                value={formData.reviewTitle}
                onChange={handleChange}
                placeholder="Summarize your experience"
                maxLength={100}
              />
              {errors.reviewTitle && <p className="error-text">{errors.reviewTitle}</p>}
            </div>

            {/* Review Text */}
            <div className="form-group">
              <label>Your Review *</label>
              <textarea
                name="review"
                className="form-control"
                rows="5"
                value={formData.review}
                onChange={handleChange}
                placeholder="Tell us about your experience..."
                maxLength={1000}
              />
              <small className="character-count">
                {formData.review.length}/1000 characters
              </small>
              {errors.review && <p className="error-text">{errors.review}</p>}
            </div>

            {/* Submit Button */}
            <button type="submit" className="btn btn-primary btn-block py-3 px-5" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>

          {/* Footer */}
          <div className="review-footer text-center mt-4">
            <p className="text-muted mb-0">Your feedback helps us improve our services</p>
          </div>
        </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Review;
